import { useCallback, useEffect, useRef, useState } from 'react';
import { useTrackCallEvent } from './useTrackCallEvent';
import { useAppDispatch, useAppSelector } from '../useReduxToolkit';
import {
  getApolloSessionDetails,
  getIsAudioCall,
} from '../../features/apolloSession/apolloSession.selectors';
import {
  TrackedEvents,
  getHasTrackedCallStart,
  getHasTrackedCallEnd,
  getTrackingState,
  setTrackedCallEnd,
  getIsHidePreview,
  getPinCount,
  getUnpinCount,
  getUserFlipCamera,
} from '../../features/tracking';
import { resetPinAndUnpinCount } from '../../features/tracking/tracking.slice';
import { type CallEndReason } from './callEndReason';
import { getIdentityId } from '../../features/realtime/realtime.selectors';
import { CallDurationStep, callDurationTimer } from './callDurationTimer';
import { timer } from '../../libs/timerObserver';
import { timerKeys } from '../../shared/constants';
import type {
  AddParticipantsStat,
  SessionClosedProps,
} from './eventProps.types';
import {
  getIsCallMenuOpen,
  getIsPictureInPictureStarted,
} from '../../features/uiState/uiState.selectors';
import { getCurrentStep } from '../../features/callTroubleshooting/callTroubleshooting.selectors';
import { useWasTurnedTo } from '../useWasTurnedTo';
import {
  getIsGalleryView,
  getIsNoiseSuppressionEnabled,
} from '../../features/call/call.selectors';
import { useLocalMedia } from '../useLocalMedia';
import { getIsModalOpen } from '../../features/modals/modals.selector';
import { ModalId } from '../../features/modals/modals.type';
import {
  getPendingParticipants,
  getSessionParticipantsIds,
} from '../../features/session/session.selectors';
import { useCallViewTime } from './useCallViewTime';
import { subscriberVideoStats } from './subscriberVideoStats';
import { useVirtualBackgroundName } from '../useVirtualBackground';
import { usePatientCallHold } from '../usePatientCallHold';
import { useService } from '../useService';
import { selectIsCaptionsOpen } from '../../features/captions/captions.selectors';
import { useValueChangeCounter } from './useValueChangeCounter';
import { otExceptionsStats } from './otExceptionsStats';
import { pubSubStatusStats } from './pubSubStatusStats';
import { autoQualityAdjustorCpuMetrics } from '../../services/call/qos';
import { StatsToLog } from '../../webrtc/session/metrics';
import { getPublisherVideoStats } from './publisherVideoStats';
import { useConnectionTest } from '../useConnectionTest';

import { useFullscreen } from '../useFullscreen';
type TrackCallEndEvent = (reason: CallEndReason) => void;

const GROUP_CALL_END_TIME_LS_VAR_NAME = 'lastGroupCallEndTime';

export const useTrackCallEndEvent = (): TrackCallEndEvent => {
  const trackCallEvent = useTrackCallEvent();

  const dispatch = useAppDispatch();
  const hasTrackedCallStart = useAppSelector(getHasTrackedCallStart);
  const hasTrackedCallEnd = useAppSelector(getHasTrackedCallEnd);

  const apolloSession = useAppSelector(getApolloSessionDetails);
  const { dbSessionId, sessionId } = apolloSession;

  const { initialMicAccess, initialCameraAccess } =
    useAppSelector(getTrackingState);

  const isAudioCall = useAppSelector(getIsAudioCall);
  const identityId = useAppSelector(getIdentityId);

  const openCallMenu = useWasTurnedTo(useAppSelector(getIsCallMenuOpen));
  const openTroubleshooting = useWasTurnedTo(
    useAppSelector(getCurrentStep) !== null,
  );
  const openSettings = useWasTurnedTo(
    useAppSelector((state) => getIsModalOpen(state, ModalId.CallSettings)),
  );

  const { isCallOnHold } = usePatientCallHold();

  const pipUsedRef = useValueChangeCounter(
    useAppSelector(getIsPictureInPictureStarted),
  );

  const advancedNoiseSuppressionUsedRef = useValueChangeCounter(
    useAppSelector(getIsNoiseSuppressionEnabled),
  );

  const closedCaptionsUsedRef = useValueChangeCounter(
    useAppSelector(selectIsCaptionsOpen),
  );

  const isGalleryView = useAppSelector(getIsGalleryView);
  const galleryViewUsedRef = useValueChangeCounter(isGalleryView);

  const { isActive: isFullscreenActive } = useFullscreen();
  const fullscreenUsedRef = useValueChangeCounter(isFullscreenActive);

  const isHidePreview = useAppSelector(getIsHidePreview);

  const userFlipCamera = useAppSelector(getUserFlipCamera);
  const pinCount = useAppSelector(getPinCount);
  const unpinCount = useAppSelector(getUnpinCount);

  const currentParticipantsIds = useAppSelector(getSessionParticipantsIds);
  const pendingParticipants = useAppSelector(getPendingParticipants);

  const { hasAudio, hasVideo, hasAudioAccess } = useLocalMedia();

  const currentPeopleCountInCall = currentParticipantsIds.length + 1;
  const [maxPeopleCountOnline, setMaxPeopleCountOnline] = useState(0);
  const [startGroupCallView, setStartGroupCallView] = useState<
    undefined | string
  >();

  const tryConnectParticipantsIds = useRef<string[]>([]);
  const lastPendingParticipantsIds = useRef<string[]>([]);
  const addParticipantStats = useRef<AddParticipantsStat>({
    fails: 0,
    retries: 0,
    tries: 0,
  });

  const backgroundEffect = useVirtualBackgroundName();

  const callService = useService('call');

  useEffect(() => {
    const pendingParticipantsIds = Object.keys(pendingParticipants);

    for (const id of lastPendingParticipantsIds.current) {
      if (
        !pendingParticipantsIds.includes(id) &&
        !currentParticipantsIds.includes(id)
      ) {
        addParticipantStats.current.fails++;
      }
    }

    for (const id of pendingParticipantsIds) {
      if (!lastPendingParticipantsIds.current.includes(id)) {
        if (tryConnectParticipantsIds.current.includes(id)) {
          addParticipantStats.current.retries++;
        } else {
          addParticipantStats.current.tries++;
          tryConnectParticipantsIds.current.push(id);
        }
      }
    }

    lastPendingParticipantsIds.current = pendingParticipantsIds;
  }, [currentParticipantsIds, pendingParticipants]);

  useEffect(() => {
    const newMaxCount = Math.max(
      currentPeopleCountInCall,
      maxPeopleCountOnline,
    );
    setMaxPeopleCountOnline(newMaxCount);

    if (startGroupCallView === undefined && newMaxCount > 2) {
      setStartGroupCallView(isGalleryView ? 'gallery' : 'speaker');
    }
  }, [
    currentPeopleCountInCall,
    isGalleryView,
    maxPeopleCountOnline,
    startGroupCallView,
  ]);

  const getCallViewTime = useCallViewTime();

  const { connectionTestResult, connectionTestError, connectionTestRawResult } =
    useConnectionTest();

  return useCallback(
    (reason) => {
      /**
       * If the session not yet started, then do not track it ended.
       */
      if (!hasTrackedCallStart) {
        return;
      }

      /**
       * If the session has already ended, then do not track duplicate.
       */
      if (hasTrackedCallEnd) {
        return;
      }

      dispatch(setTrackedCallEnd());

      const metrics = callService.getMetrics() as StatsToLog;

      callDurationTimer.finish(CallDurationStep.End);
      const callDuration = callDurationTimer.getDurationOfTimer();

      const callStartTimings = timer.getResult(timerKeys.callInit);

      const isGroupCall = maxPeopleCountOnline > 2;

      if (isGroupCall) {
        localStorage.setItem(
          GROUP_CALL_END_TIME_LS_VAR_NAME,
          Date.now().toString(),
        );
      }

      const participantVideoDisabledTrackingData =
        sessionId !== undefined ? subscriberVideoStats.getStats(sessionId) : {};

      const data: SessionClosedProps = {
        ...participantVideoDisabledTrackingData,
        ...getPublisherVideoStats(sessionId),
        ...metrics,
        ...autoQualityAdjustorCpuMetrics,
        otExceptionsStats: otExceptionsStats.finalizeAndGetStats(),
        ...pubSubStatusStats.finalizeAndGetStats(),
        reason,
        duration: callDuration,
        delayBeforeStart: callStartTimings.total,
        sessionId,
        dbSessionId,
        personUuid: identityId,
        onStartHasCameraAccess: initialCameraAccess,
        onStartHasMicrophoneAccess: initialMicAccess,
        audioOnly: isAudioCall,
        isGroupCall,
        maxPeopleCountOnline,
        addParticipantStat: addParticipantStats.current,
        isCallOnHold,
        advancedNoiseSuppressionUsed: advancedNoiseSuppressionUsedRef.current,
        closedCaptionsUsed: closedCaptionsUsedRef.current,
        callDefinition: callService.manualVideoQuality,
        connectionTestResult,
        connectionTestError,
        connectionTestRawResult,
        galleryViewUsed: galleryViewUsedRef.current,
        fullscreenUsed: fullscreenUsedRef.current,
      };

      const segmentData: SessionClosedProps = {
        ...participantVideoDisabledTrackingData,
        ...getPublisherVideoStats(sessionId),
        ...metrics,
        ...autoQualityAdjustorCpuMetrics,
        reason,
        duration: callDuration,
        delayBeforeStart: callStartTimings.total,
        sessionId: dbSessionId,
        channel: sessionId,
        personUuid: identityId,
        onStartHasCameraAccess: initialCameraAccess,
        onStartHasMicrophoneAccess: initialMicAccess,
        audioOnly: isAudioCall,
        openCallMenu,
        openTroubleshooting,
        openSettings,
        galleryView: isGalleryView,
        galleryViewUsed: galleryViewUsedRef.current,
        standardView: !isGalleryView,
        fullscreenUsed: fullscreenUsedRef.current,
        mute: !hasAudio,
        unmute: hasAudio,
        videoEnabled: hasVideo,
        videoDisabled: !hasVideo,
        noSoundDetected: !hasAudioAccess,
        userFlipCamera,
        pin: pinCount,
        unpin: unpinCount,
        hidePreview: isHidePreview,
        showPreview: !isHidePreview,
        pipUsed: pipUsedRef.current,
        advancedNoiseSuppressionUsed: advancedNoiseSuppressionUsedRef.current,
        closedCaptionsUsed: closedCaptionsUsedRef.current,
        backgroundEffect,
        callDefinition: callService.manualVideoQuality,
        ...(isGroupCall
          ? {
              maxPeopleCountOnline,
              startGroupCallView,
              viewDurations: getCallViewTime(),
            }
          : {}),
        isCallOnHold,
        ...pubSubStatusStats.finalizeAndGetStats(),
      };

      trackCallEvent(TrackedEvents.EndCall, data, segmentData);

      dispatch(resetPinAndUnpinCount());
      pipUsedRef.current = 0;
      advancedNoiseSuppressionUsedRef.current = 0;
      closedCaptionsUsedRef.current = 0;
      galleryViewUsedRef.current = 0;
      fullscreenUsedRef.current = 0;
    },
    [
      dbSessionId,
      dispatch,
      hasAudio,
      hasAudioAccess,
      hasTrackedCallEnd,
      hasTrackedCallStart,
      hasVideo,
      identityId,
      initialCameraAccess,
      initialMicAccess,
      isAudioCall,
      isGalleryView,
      isHidePreview,
      openCallMenu,
      openSettings,
      openTroubleshooting,
      pinCount,
      sessionId,
      trackCallEvent,
      unpinCount,
      userFlipCamera,
      maxPeopleCountOnline,
      startGroupCallView,
      getCallViewTime,
      backgroundEffect,
    ],
  );
};
